import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import MarketingNav from "@/components/site/MarketingNav";
import Footer from "@/components/site/Footer";
import HomepageVideo from "@/components/site/HomepageVideo";
import FeaturedCreations from "@/components/site/FeaturedCreations";
import TrustStrip from "@/components/site/TrustStrip";
import CreditsCalculator from "@/components/site/CreditsCalculator";
import { api } from "@/lib/api";
import {
  ArrowRight,
  Sparkles,
  FileText,
  ImageIcon,
  Film,
  Mail,
  Search,
  Instagram,
  Coins,
  Clock,
  Gift,
  Shield,
  Zap,
  Type,
} from "lucide-react";

/* -------------------------------------------------------------------------- */
/*  Self-Demonstrating Showcase — pre-rendered outputs (no LLM cost per view) */
/* -------------------------------------------------------------------------- */
const SHOWCASE = {
  idea: "I'm opening a queer-owned coffee shop in Los Angeles.",
  outputs: [
    {
      kind: "BLOG",
      icon: FileText,
      title: "Why Silverlake Needs a Third Place That Feels Like Home",
      body:
        "In Los Angeles, coffee is not a beverage. It is a social contract. And for the queer community, a good third place is a survival tool as much as a comfort. Our shop — opening this fall on Sunset — is being built to hold that weight without pretending to be neutral. This is why…",
      meta: "5-min read · Editorial · 862 words",
    },
    {
      kind: "SOCIAL",
      icon: Instagram,
      title: "@yourshop — Instagram caption",
      body:
        "we're not another coffee shop.\n\nwe're a room where you don't have to explain yourself.\n\nopening this fall in silverlake.\nqueer-owned. queer-run. queer-loved.\n\ndrop a 🌈 if you're pulling up on day one.",
      meta: "Instagram · 42 words · 6 hashtags queued",
    },
    {
      kind: "EMAIL",
      icon: Mail,
      title: "Subject: We built this for you (finally).",
      body:
        "Hi friend —\n\nAfter three years of pop-ups, we're finally signing a lease. A permanent, unapologetically queer coffee shop in Silverlake.\n\nFounding members get: first dibs on the opening weekend, a lifetime discount, and your name on the wall.\n\n32 seats. That's the number.\n\n[Claim yours →]",
      meta: "Marketing email · 68 words · 1 clear CTA",
    },
    {
      kind: "SEO",
      icon: Search,
      title: "queer coffee shop los angeles",
      body:
        "Title: Queer-Owned Coffee Shop in Silverlake, LA — [Your Brand]\nMeta: A permanent third place for LA's queer community. Opening fall 2026 in Silverlake. Lock in founding member benefits before doors open.\nKeywords: queer coffee shop LA, LGBTQ cafe silverlake, gay owned coffee los angeles, queer third place\nOutline: H2 The neighborhood · H2 The founding member offer · H2 Meet the founders · H2 Visit us",
      meta: "SEO pack · 60/155 char compliant",
    },
    {
      kind: "IMAGE",
      icon: ImageIcon,
      title: "Editorial header · warm morning light",
      body: "https://images.unsplash.com/photo-1509042239860-f550ce710b93?w=900&auto=format&fit=crop&q=80",
      meta: "GPT Image 1 · 1024 × 1024 · magazine grade",
    },
    {
      kind: "VIDEO",
      icon: Film,
      title: "5-sec cinematic loop · steam & sunlight",
      body: "poster",
      meta: "Sora 2 · 5s · 1080p · autoplays on load",
    },
  ],
};

function Showcase() {
  const [revealed, setRevealed] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    if (revealed >= SHOWCASE.outputs.length) return;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting && revealed === 0) {
          setRevealed(1);
        }
      },
      { threshold: 0.25 }
    );
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, [revealed]);

  useEffect(() => {
    if (revealed === 0 || revealed >= SHOWCASE.outputs.length) return;
    const t = setTimeout(() => setRevealed((r) => r + 1), 700);
    return () => clearTimeout(t);
  }, [revealed]);

  return (
    <section
      ref={ref}
      className="border-b border-border bg-foreground text-background"
      data-testid="showcase-section"
    >
      <div className="max-w-[1400px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 border-b border-background/10">
          <div className="lg:col-span-5 p-8 md:p-14 border-r border-background/10 lg:sticky lg:top-0 lg:self-start lg:h-screen lg:overflow-y-auto lg:flex lg:flex-col lg:justify-center">
            <div className="overline opacity-60">A live demonstration</div>
            <h2 className="font-serif-display text-4xl md:text-6xl leading-none tracking-tight mt-4">
              One idea.
              <br />
              <span className="italic text-primary">Six deliverables.</span>
            </h2>
            <p className="mt-6 opacity-80 max-w-md">
              We handed Draftwave one line. It gave us a week's worth of publish-ready content.
              You get to do the same — with your own idea — in the workspace.
            </p>
            <div className="mt-8 border border-background/20 p-5 font-mono text-sm">
              <div className="overline opacity-50 text-[10px]">The prompt</div>
              <div className="mt-3 text-primary">
                <span className="opacity-60">$</span> {SHOWCASE.idea}
              </div>
            </div>
            <Link to="/workspace" className="mt-8 inline-block" data-testid="showcase-cta">
              <Button className="rounded-none h-12 px-6 bg-primary text-primary-foreground hover:bg-primary/90 font-mono uppercase tracking-widest text-xs">
                Your turn — 120 free credits <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
          <div className="lg:col-span-7 divide-y divide-background/10">
            {SHOWCASE.outputs.map((o, i) => {
              const Icon = o.icon;
              const show = i < revealed;
              return (
                <div
                  key={o.kind}
                  className={`p-6 md:p-8 transition-all duration-700 ${
                    show ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
                  }`}
                  data-testid={`showcase-item-${o.kind.toLowerCase()}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Icon className="w-4 h-4 text-primary" />
                      <span className="overline text-[10px] opacity-60">{o.kind}</span>
                    </div>
                    <span className="font-mono text-[10px] uppercase tracking-widest opacity-40">
                      {String(i + 1).padStart(2, "0")} / 06
                    </span>
                  </div>
                  <div className="mt-4 font-serif-display text-xl md:text-2xl leading-tight">
                    {o.title}
                  </div>
                  {o.kind === "IMAGE" ? (
                    <img
                      src={o.body}
                      alt="Editorial mockup"
                      className="mt-4 w-full max-h-56 object-cover border border-background/10"
                    />
                  ) : o.kind === "VIDEO" ? (
                    <div className="mt-4 aspect-video border border-background/10 relative overflow-hidden bg-background/5">
                      <img
                        src="https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?w=1200&auto=format&fit=crop&q=80"
                        alt="Video poster"
                        className="absolute inset-0 w-full h-full object-cover opacity-70"
                      />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-14 h-14 rounded-full bg-primary/90 flex items-center justify-center">
                          <Film className="w-5 h-5 text-primary-foreground" />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <pre className="mt-3 whitespace-pre-wrap font-mono text-[13px] leading-relaxed opacity-90">
                      {o.body}
                    </pre>
                  )}
                  <div className="mt-3 overline text-[10px] opacity-50">{o.meta}</div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="p-8 md:p-14 text-center">
          <div className="overline opacity-50">One idea. Six deliverables.</div>
          <div className="font-serif-display text-3xl md:text-5xl mt-4 leading-tight">
            Your turn.
          </div>
          <Link to="/workspace" data-testid="showcase-final-cta">
            <Button className="mt-6 rounded-none h-12 px-8 bg-primary text-primary-foreground hover:bg-primary/90 font-mono uppercase tracking-widest text-xs">
              Start with 120 free credits <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Cost strip                                                                */
/* -------------------------------------------------------------------------- */
const COST_TILES = [
  {
    icon: Type,
    cost: "1c",
    title: "Text",
    body: "Blog · Social · Email · SEO · Ideas · Captions",
  },
  {
    icon: ImageIcon,
    cost: "5c",
    title: "Image",
    body: "Editorial visuals & magazine-grade social art",
  },
  {
    icon: Film,
    cost: "40c",
    title: "Video",
    body: "Sora 2 · 5-second cinematic clips",
  },
];

export default function Landing() {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    if (ref) {
      localStorage.setItem("draftwave.ref_code", ref.toLowerCase());
      api.post(`/referral/click?code=${encodeURIComponent(ref)}`).catch(() => {});
    }
  }, []);

  return (
    <div className="min-h-screen bg-background" data-testid="landing-page">
      <MarketingNav />

      {/* ---------------- HERO ---------------- */}
      <section className="border-b border-border grain relative overflow-hidden">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-0">
          <div className="lg:col-span-8 border-r border-border p-8 md:p-16 lg:p-20 relative">
            <div className="overline text-muted-foreground mb-8 reveal reveal-1">
              Vol. 01 — Issue 002 — Momentum, not AI
            </div>
            <h1
              className="font-serif-display text-5xl sm:text-6xl md:text-7xl lg:text-[7.5rem] leading-[0.9] tracking-tight reveal reveal-2"
              data-testid="hero-headline"
            >
              A trillion
              <br />
              starts with
              <br />
              a <span className="italic text-primary">draft.</span>
            </h1>
            <p className="mt-10 max-w-2xl text-base md:text-lg text-muted-foreground leading-relaxed reveal reveal-3">
              Turn one idea into a week of publish-ready content. Blogs. Social. Email. SEO.
              Images. Video. All from one editorial workspace — no more paying five different tools
              to say the same thing.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4 reveal reveal-4">
              <Link to="/login" data-testid="hero-cta-signup">
                <Button
                  size="lg"
                  className="rounded-none h-14 px-8 font-mono uppercase tracking-widest text-xs group"
                >
                  Get 120 free credits
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
              <Link to="/workspace" data-testid="hero-cta-workspace">
                <Button
                  size="lg"
                  variant="outline"
                  className="rounded-none h-14 px-8 font-mono uppercase tracking-widest text-xs"
                >
                  See the workspace
                </Button>
              </Link>
              <div className="overline text-muted-foreground text-[11px] tracking-widest">
                No credit card · 120 free credits · Cancel anytime
              </div>
            </div>
            <div className="mt-16 grid grid-cols-3 gap-0 border-t border-border">
              {[
                { k: "120", v: "free credits at signup" },
                { k: "5", v: "free credits every week" },
                { k: "8 min", v: "to ship a week of copy" },
              ].map((s, i) => (
                <div
                  key={s.v}
                  className={`p-6 ${i < 2 ? "border-r border-border" : ""}`}
                  data-testid={`hero-stat-${i}`}
                >
                  <div className="font-serif-display text-4xl md:text-5xl leading-none">{s.k}</div>
                  <div className="overline text-muted-foreground mt-2">{s.v}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-4 flex flex-col">
            <div className="flex-1 border-b border-border relative min-h-[280px] overflow-hidden">
              <img
                src="https://images.pexels.com/photos/25949753/pexels-photo-25949753.jpeg?auto=compress&cs=tinysrgb&w=900"
                alt="Editorial texture"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-background/40" />
              <div className="absolute bottom-6 left-6 right-6">
                <div className="overline text-background mix-blend-difference">Not selling AI</div>
                <div className="font-serif-display text-2xl text-background mix-blend-difference mt-2 leading-tight">
                  Selling momentum.
                </div>
              </div>
            </div>
            <div className="p-8">
              <div className="overline text-muted-foreground">Pull quote</div>
              <blockquote className="mt-3 font-serif-display text-2xl md:text-3xl leading-tight">
                "You have the idea.
                <br />
                Draftwave helps you{" "}
                <span className="italic text-primary">ship</span> it."
              </blockquote>
              <div className="mt-4 text-xs font-mono text-muted-foreground uppercase tracking-widest">
                — The Draftwave manifesto
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- TRUST STRIP · conversion ammunition ---------------- */}
      <TrustStrip />

      {/* ---------------- CREDITS CALCULATOR · "what could I actually make?" ---------------- */}
      <CreditsCalculator />

      {/* ---------------- CINEMATIC VIDEO — 30 SEC DRAFTWAVE FILM ---------------- */}
      <HomepageVideo />

      {/* ---------------- FEATURED CREATIONS — user-submitted, admin-approved ---------------- */}
      <FeaturedCreations />

      {/* ---------------- SECTION 2 · WORKSPACE PROMISE ---------------- */}
      <section className="border-b border-border">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12">
          <div className="lg:col-span-5 border-r border-border p-8 md:p-14">
            <div className="overline text-muted-foreground">The workspace</div>
            <h2 className="font-serif-display text-4xl md:text-6xl leading-none tracking-tight mt-4">
              One idea.
              <br />
              <span className="italic text-primary">A whole week</span>
              <br />
              of content.
            </h2>
            <p className="mt-6 max-w-md text-muted-foreground">
              Give Draftwave one idea and turn it into blogs, social posts, emails, SEO content,
              images and cinematic video. You have 120 free credits waiting. No credit card
              required.
            </p>
            <Link to="/workspace" data-testid="workspace-promise-cta" className="mt-8 inline-block">
              <Button className="rounded-none h-12 px-6 font-mono uppercase tracking-widest text-xs">
                Start creating free <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
          <div className="lg:col-span-7 bg-foreground text-background p-8 md:p-14">
            <div className="border border-background/20 p-6 md:p-8 font-mono text-sm">
              <div className="flex items-center justify-between overline opacity-60 text-[10px]">
                <span>DRAFTWAVE · WORKSPACE PREVIEW</span>
                <span>20 CREDITS</span>
              </div>
              <div className="mt-6 opacity-80">
                <span className="opacity-50">&gt; </span>What are you promoting?
              </div>
              <div className="mt-2 text-primary">
                <span className="opacity-50">&gt; </span>My new mobile detailing business
              </div>
              <div className="mt-6 grid grid-cols-3 gap-2">
                {["BLOG", "SOCIAL", "EMAIL", "SEO", "IMAGE", "VIDEO"].map((t) => (
                  <div
                    key={t}
                    className="border border-background/20 p-3 text-[10px] uppercase tracking-widest opacity-80 hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors"
                  >
                    {t}
                  </div>
                ))}
              </div>
              <div className="mt-6 overline opacity-60 text-[10px]">→ Click. Boom. That's the aha.</div>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- SECTION 3 · TYPE IT WATCH IT BUILD ---------------- */}
      <section className="border-b border-border bg-background">
        <div className="max-w-[1400px] mx-auto p-8 md:p-14 text-center">
          <div className="overline text-muted-foreground">The rhythm</div>
          <h2 className="font-serif-display text-5xl md:text-7xl leading-none tracking-tight mt-4">
            Type it.
            <br />
            <span className="italic text-primary">Watch it build.</span>
          </h2>
          <p className="mt-6 max-w-xl mx-auto text-muted-foreground">
            The workspace is not a chat window. It's an editorial console. Text, image and video
            outputs live on the same canvas.
          </p>
        </div>
      </section>

      {/* ---------------- SECTION 4 · WRITE DESIGN MOVE ---------------- */}
      <section className="border-b border-border">
        <div className="max-w-[1400px] mx-auto">
          <div className="p-8 md:p-14 border-b border-border">
            <div className="overline text-muted-foreground">Pay for what you create</div>
            <h2 className="font-serif-display text-4xl md:text-6xl leading-none tracking-tight mt-4">
              Write. Design.
              <br />
              <span className="italic text-primary">Move.</span>
            </h2>
            <p className="mt-6 max-w-2xl text-muted-foreground">
              Text is cheap. Images cost more. Video uses more compute. Your credits never expire.
              And you start with 120 free.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 border-l border-border">
            {COST_TILES.map((t, i) => {
              const Icon = t.icon;
              const dark = i === 1;
              return (
                <div
                  key={t.title}
                  className={`p-8 md:p-12 border-r border-b border-border ${
                    dark ? "bg-foreground text-background" : ""
                  }`}
                  data-testid={`cost-tile-${t.title.toLowerCase()}`}
                >
                  <div className="flex items-center justify-between">
                    <Icon className="w-6 h-6 text-primary" />
                    <span className="font-serif-display text-3xl">{t.cost}</span>
                  </div>
                  <h3 className="font-serif-display text-3xl mt-8 leading-tight">{t.title}</h3>
                  <p className="mt-3 text-sm opacity-70">{t.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ---------------- SECTION 5 · SHOWCASE (Self-demonstration) ---------- */}
      <Showcase />

      {/* ---------------- SECTION 6 · 20 FREE ON US ---------------- */}
      <section className="border-b border-border">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-2">
          <div className="p-10 md:p-16 border-r border-border">
            <div className="overline text-muted-foreground flex items-center gap-2">
              <Gift className="w-3 h-3" /> On the house
            </div>
            <h2 className="font-serif-display text-4xl md:text-6xl leading-none tracking-tight mt-4">
              Your first
              <br />
              <span className="italic text-primary">120 credits</span>
              <br />
              are on us.
            </h2>
            <p className="mt-6 max-w-md text-muted-foreground">
              Try the actual workspace. No credit card. No sales call. No guessing what Draftwave
              does.
            </p>
            <Link to="/login" data-testid="free-cta" className="mt-8 inline-block">
              <Button className="rounded-none h-12 px-6 font-mono uppercase tracking-widest text-xs">
                Start free <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
          <div className="p-10 md:p-16 bg-foreground text-background flex flex-col justify-between gap-8">
            <div>
              <div className="overline opacity-60 flex items-center gap-2">
                <Clock className="w-3 h-3" /> The 8-minute challenge
              </div>
              <h3 className="font-serif-display text-3xl md:text-5xl leading-none mt-4">
                Can you make a week of content
                <br />
                <span className="italic text-primary">before your coffee gets cold?</span>
              </h3>
            </div>
            <div className="grid grid-cols-4 gap-2 font-mono text-[10px] uppercase tracking-widest">
              {[
                ["00:00", "Idea"],
                ["01:00", "Blog"],
                ["02:00", "Social"],
                ["03:00", "Email"],
                ["04:00", "SEO"],
                ["05:00", "Visual"],
                ["06:00", "Video"],
                ["08:00", "Ship"],
              ].map(([t, l]) => (
                <div key={t} className="border border-background/20 p-3">
                  <div className="text-primary">{t}</div>
                  <div className="opacity-70 mt-1">{l}</div>
                </div>
              ))}
            </div>
            <Link to="/workspace?challenge=1" data-testid="challenge-cta">
              <Button className="rounded-none h-12 px-6 bg-primary text-primary-foreground hover:bg-primary/90 font-mono uppercase tracking-widest text-xs w-full sm:w-auto">
                Start the challenge <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ---------------- SECTION 7 · GO FURTHER (subscription) ------------- */}
      <section className="border-b border-border">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-12">
          <div className="md:col-span-5 border-r border-border p-10 md:p-14">
            <div className="overline text-muted-foreground">When you're ready</div>
            <h2 className="font-serif-display text-4xl md:text-6xl leading-none tracking-tight mt-4">
              Go further.
            </h2>
            <p className="mt-6 max-w-md text-muted-foreground">
              Credits are fuel. Subscription is home. The ongoing Draftwave experience — priority
              routing, higher monthly volume, editorial support — starts at $19/month.
            </p>
            <Link to="/pricing" data-testid="see-plans-cta" className="mt-8 inline-block">
              <Button
                variant="outline"
                className="rounded-none h-12 px-6 font-mono uppercase tracking-widest text-xs"
              >
                See plans <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
          <div className="md:col-span-7 grid grid-cols-3">
            {[
              { p: "$19", n: "Starter", tag: "Weekly creators" },
              { p: "$49", n: "Pro", tag: "Daily publishing", dark: true },
              { p: "$149", n: "Studio", tag: "Full production" },
            ].map((t, i) => (
              <div
                key={t.n}
                className={`p-8 md:p-12 border-r border-b border-border ${
                  t.dark ? "bg-foreground text-background" : ""
                } ${i === 2 ? "border-r-0" : ""}`}
                data-testid={`plan-preview-${t.n.toLowerCase()}`}
              >
                <div className="overline opacity-60">{t.n}</div>
                <div className="font-serif-display text-5xl md:text-6xl leading-none mt-4">
                  {t.p}
                </div>
                <div className="overline opacity-60 mt-2">/mo</div>
                <div className="mt-6 text-xs font-mono uppercase tracking-widest opacity-70">
                  {t.tag}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- SECTION 8 · REFERRAL DOOR ---------------- */}
      <section className="border-b border-border bg-foreground text-background">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-2">
          <div className="p-10 md:p-16 border-r border-background/10">
            <div className="overline opacity-60 flex items-center gap-2">
              <Sparkles className="w-3 h-3 text-primary" /> Love Draftwave?
            </div>
            <h2 className="font-serif-display text-4xl md:text-6xl leading-none tracking-tight mt-4">
              Get paid to
              <br />
              <span className="italic text-primary">share it.</span>
            </h2>
            <p className="mt-6 opacity-80 max-w-md">
              Earn <span className="text-primary font-mono">30%</span> recurring commission every
              time someone you refer pays their monthly subscription. Not a one-time bounty —
              recurring, for as long as they stay.
            </p>
            <Link to="/login" data-testid="referral-cta" className="mt-8 inline-block">
              <Button className="rounded-none h-12 px-6 bg-primary text-primary-foreground hover:bg-primary/90 font-mono uppercase tracking-widest text-xs">
                Start earning <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
          <div className="p-10 md:p-16 grid grid-cols-2 gap-0 border-l border-background/10">
            {[
              { k: "30%", v: "Recurring" },
              { k: "$0", v: "To join" },
              { k: "∞", v: "Referrals" },
              { k: "Monthly", v: "Payouts" },
            ].map((s, i) => (
              <div
                key={s.v}
                className={`p-6 md:p-8 border-r border-b border-background/10 ${
                  i % 2 === 1 ? "border-r-0" : ""
                } ${i > 1 ? "border-b-0" : ""}`}
              >
                <div className="font-serif-display text-4xl md:text-5xl leading-none text-primary">
                  {s.k}
                </div>
                <div className="overline opacity-60 mt-2 text-[10px]">{s.v}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Referral math — spelled out so nobody confuses top-line revenue with referrer earnings */}
        <div className="max-w-[1400px] mx-auto border-t border-background/10 grid grid-cols-1 md:grid-cols-2" data-testid="referral-math">
          <div className="p-10 md:p-14 border-r border-background/10">
            <div className="overline opacity-60 text-[10px]">The math · Customer revenue</div>
            <div className="mt-4 font-mono text-sm opacity-80 space-y-1">
              <div>1,000 subs × $19/mo = <span className="text-primary">$19,000 MRR</span></div>
              <div>$19,000 × 12 months =</div>
            </div>
            <div className="font-serif-display text-4xl md:text-6xl leading-none tracking-tight mt-4 text-primary" data-testid="referral-math-customer">
              $228,000/yr
            </div>
            <div className="mt-3 text-xs opacity-70 max-w-sm">
              Top-line subscription revenue produced by 1,000 subscribers. This is what the business
              earns — not what you personally receive as a referrer.
            </div>
          </div>
          <div className="p-10 md:p-14 border-l border-background/10 bg-primary/5">
            <div className="overline opacity-60 text-[10px]">Your cut · Referrer earnings</div>
            <div className="mt-4 font-mono text-sm opacity-80 space-y-1">
              <div>1,000 referred × $19 × 30% = <span className="text-primary">$5,700/mo</span></div>
              <div>$5,700 × 12 months =</div>
            </div>
            <div className="font-serif-display text-4xl md:text-6xl leading-none tracking-tight mt-4 text-primary" data-testid="referral-math-referrer">
              $68,400/yr
            </div>
            <div className="mt-3 text-xs opacity-70 max-w-sm">
              What Draftwave pays you at 30% recurring commission, if all 1,000 referred subscribers
              stayed on the $19 plan for a full year.
            </div>
          </div>
          <div className="md:col-span-2 border-t border-background/10 px-10 md:px-14 py-5 text-[11px] font-mono uppercase tracking-widest opacity-60" data-testid="referral-math-disclaimer">
            Illustrative example only — not a guarantee of earnings. Actual results depend on plan
            mix, retention, refunds, and how many subscribers you refer.
          </div>
        </div>
      </section>

      {/* ---------------- SECTION 9 · FREE TOOLS TEASER ---------------- */}
      <section className="border-b border-border">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-12">
          <div className="md:col-span-4 border-r border-border p-10 md:p-14">
            <div className="overline text-muted-foreground">Free · No signup</div>
            <h2 className="font-serif-display text-4xl md:text-6xl leading-none tracking-tight mt-4">
              Try a
              <br />
              <span className="italic text-primary">free tool.</span>
            </h2>
            <p className="mt-6 max-w-sm text-muted-foreground">
              Six pocket-sized generators — no account required. When you're ready to publish a
              whole week, the workspace is one click away.
            </p>
            <Link to="/tools" data-testid="tools-cta" className="mt-8 inline-block">
              <Button
                variant="outline"
                className="rounded-none h-12 px-6 font-mono uppercase tracking-widest text-xs"
              >
                Open free tools <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
          <div className="md:col-span-8 grid grid-cols-2 md:grid-cols-3 border-l border-t border-border">
            {[
              { i: FileText, t: "Blog idea generator", to: "/tools#blog" },
              { i: Instagram, t: "Social caption generator", to: "/tools#social" },
              { i: Mail, t: "Marketing email generator", to: "/tools#email" },
              { i: Search, t: "SEO meta generator", to: "/tools#seo" },
              { i: Sparkles, t: "7-Day content calendar", to: "/free-week" },
              { i: Film, t: "Video prompt generator", to: "/tools#video" },
            ].map((t) => {
              const Icon = t.i;
              return (
                <Link
                  key={t.t}
                  to={t.to}
                  className="p-6 border-r border-b border-border hover:bg-foreground hover:text-background transition-colors group"
                  data-testid={`tool-tile-${t.t.split(" ")[0].toLowerCase()}`}
                >
                  <Icon className="w-5 h-5 text-primary" />
                  <div className="font-serif-display text-xl mt-6 leading-tight">{t.t}</div>
                  <div className="mt-4 overline text-[10px] group-hover:text-background flex items-center gap-1">
                    Free tool <ArrowRight className="w-3 h-3" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ---------------- SECTION 10 · PRIVACY / GUARANTEE STRIP ---------- */}
      <section className="border-b border-border">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-3">
          {[
            {
              icon: Shield,
              t: "Your prompts stay yours",
              b: "We do not sell your prompts. Your content is not used to train models.",
            },
            {
              icon: Coins,
              t: "Credits never expire",
              b: "One-time or subscription — no burn-down clock, no fine print.",
            },
            {
              icon: Zap,
              t: "Ship in minutes, not weeks",
              b: "A week of copy in 8 minutes. On average. Actual mileage may vary.",
            },
          ].map((f, i) => {
            const Icon = f.icon;
            return (
              <div
                key={f.t}
                className={`p-8 md:p-12 ${i < 2 ? "border-r border-border" : ""}`}
                data-testid={`guarantee-${i}`}
              >
                <Icon className="w-6 h-6 text-primary" />
                <h4 className="font-serif-display text-2xl md:text-3xl mt-6 leading-tight">
                  {f.t}
                </h4>
                <p className="mt-3 text-sm text-muted-foreground">{f.b}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* ---------------- FINAL CTA ---------------- */}
      <section className="border-b border-border">
        <div className="max-w-[1400px] mx-auto p-10 md:p-24 text-center md:text-left flex flex-col md:flex-row items-start md:items-end justify-between gap-10">
          <div>
            <div className="overline text-muted-foreground">Final word</div>
            <h2 className="font-serif-display text-5xl md:text-7xl leading-none tracking-tight mt-6">
              You have the idea.
              <br />
              <span className="italic text-primary">Ship it.</span>
            </h2>
          </div>
          <Link to="/login" data-testid="cta-final">
            <Button
              size="lg"
              className="rounded-none h-16 px-10 font-mono uppercase tracking-widest text-sm"
            >
              Get 120 free credits
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
